import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useI18n } from '../context/I18nContext';
import { useToast } from '../context/ToastContext';
import { Search, ChevronLeft, ChevronRight, Download, Loader, Phone, StickyNote, ArrowUp, ArrowDown, Star, Printer, X } from 'lucide-react';
import { Skeleton } from '../components/Skeleton';
import { formatDuration } from '../utils';

function SortableTh({ field, sort, order, onSort, children }) {
  const active = sort === field;
  const handleClick = () => {
    if (!active) {
      onSort(field, 'desc');
    } else {
      onSort(field, order === 'desc' ? 'asc' : 'desc');
    }
  };
  return (
    <th
      onClick={handleClick}
      className={`text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase cursor-pointer select-none transition-colors ${
        active ? 'text-white' : 'text-slate-500 hover:text-slate-300'
      }`}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (order === 'desc' ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}
      </span>
    </th>
  );
}

export default function CallLog() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10));
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [intent, setIntent] = useState(() => searchParams.get('intent') || '');
  const [sentiment, setSentiment] = useState(() => searchParams.get('sentiment') || '');
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('from') || '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('to') || '');
  const [hasNotes, setHasNotes] = useState(() => searchParams.get('has_notes') === 'true');
  const [showStarredOnly, setShowStarredOnly] = useState(() => searchParams.get('starred') === 'true');
  const [starredIds, setStarredIds] = useState(() => {
    try {
      const raw = localStorage.getItem('starredCalls');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const searchInputRef = useRef(null);
  // Keyboard nav (selected row index) + bulk-select state
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());

  // Keyboard shortcuts: '/' focus search, ArrowUp/Down navigate rows, Enter open, Escape clear.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        if (e.key === 'Escape' && tag === 'input' && e.target === searchInputRef.current) {
          setSearch('');
          setPage(1);
        }
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      const visible = (showStarredOnly ? calls.filter(c => starredIds.has(c.call_id)) : calls);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(i => Math.min((i < 0 ? -1 : i) + 1, visible.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && selectedIdx >= 0 && visible[selectedIdx]) {
        e.preventDefault();
        navigate(`/calls/${visible[selectedIdx].call_id}`);
      } else if (e.key === 'Escape') {
        if (selectedIdx >= 0) setSelectedIdx(-1);
        if (bulkSelected.size > 0) setBulkSelected(new Set());
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [calls, starredIds, showStarredOnly, selectedIdx, bulkSelected, navigate]);

  // Reset selection when filters / page change
  useEffect(() => { setSelectedIdx(-1); }, [page, search, intent, sentiment, dateFrom, dateTo, hasNotes, sort, order]);

  const visibleCalls = (showStarredOnly ? calls.filter(c => starredIds.has(c.call_id)) : calls);
  const allChecked = visibleCalls.length > 0 && visibleCalls.every(c => bulkSelected.has(c.call_id));
  const toggleBulkOne = (callId, e) => {
    if (e) e.stopPropagation();
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId); else next.add(callId);
      return next;
    });
  };
  const toggleBulkAll = () => {
    setBulkSelected(prev => {
      if (allChecked) return new Set();
      const next = new Set(prev);
      visibleCalls.forEach(c => next.add(c.call_id));
      return next;
    });
  };
  const exportSelectedCSV = () => {
    if (bulkSelected.size === 0) return;
    const rows = [['Call ID', 'Navn', 'Telefon', 'Henvendelse', 'Lead', 'Stemning', 'Varighet (s)', 'Tidspunkt']];
    visibleCalls.filter(c => bulkSelected.has(c.call_id)).forEach(call => {
      const _ca = call.call_analysis || {}; const a = { ..._ca, ...(_ca.custom_analysis_data || {}) };
      rows.push([
        call.call_id || '',
        (call.caller_name || a.caller_name || '').replace(/"/g, '""'),
        call.from_number || '',
        a.call_intent || '',
        String(call.lead_score ?? a.lead_score ?? ''),
        a.sentiment || '',
        String(call.duration_seconds || ''),
        call.created_at || '',
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `calls-selected-${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link); link.click(); link.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Eksporterte ${bulkSelected.size} samtaler`);
  };
  const handlePrint = () => {
    document.body.classList.add('print-calls-mode');
    window.print();
    setTimeout(() => document.body.classList.remove('print-calls-mode'), 1000);
  };

  const toggleStar = (callId, e) => {
    if (e) e.stopPropagation();
    setStarredIds(prev => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId); else next.add(callId);
      try { localStorage.setItem('starredCalls', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set('search', search);
      if (intent) params.set('intent', intent);
      if (sentiment) params.set('sentiment', sentiment);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo + 'T23:59:59');
      if (hasNotes) params.set('has_notes', 'true');
      if (sort) params.set('sort', sort);
      if (order) params.set('order', order);
      const { data } = await api.get(`/api/calls?${params}`);
      setCalls(data.calls);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      console.error(err);
      toast.error('Kunne ikke laste samtaler');
    } finally {
      setLoading(false);
    }
  }, [page, search, intent, sentiment, dateFrom, dateTo, hasNotes, sort, order]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  // Sync filter state → URL so filters persist on refresh and are shareable
  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (intent) params.intent = intent;
    if (sentiment) params.sentiment = sentiment;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (hasNotes) params.has_notes = 'true';
    if (showStarredOnly) params.starred = 'true';
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [search, intent, sentiment, dateFrom, dateTo, hasNotes, showStarredOnly, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: 'csv' });
      if (search) params.set('search', search);
      if (intent) params.set('intent', intent);
      if (sentiment) params.set('sentiment', sentiment);
      if (dateFrom) params.set('start_date', dateFrom);
      if (dateTo) params.set('end_date', dateTo + 'T23:59:59');
      const response = await api.get(`/api/calls/export?${params}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `calls-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('calls_export_success'));
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Export ikke tilgjengelig ennå');
      } else {
        toast.error(t('calls_export_error'));
      }
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('nb-NO', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // formatDuration imported from utils.js

  return (
    <div data-testid="call-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{t('calls_title')}</h1>
          <p className="text-slate-500 text-sm mt-1">{total} samtaler totalt</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            ref={searchInputRef}
            data-testid="call-search-input"
            type="text"
            placeholder={t('calls_search') + '  ( / )'}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
          />
        </div>
        <select
          data-testid="call-intent-filter"
          value={intent}
          onChange={(e) => { setIntent(e.target.value); setPage(1); }}
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none cursor-pointer"
        >
          <option value="">Alle henvendelser</option>
          <option value="Booking">Booking</option>
          <option value="Forespørsel">Forespørsel</option>
          <option value="Prisforespørsel">Prisforespørsel</option>
          <option value="Klage">Klage</option>
          <option value="Avbestilling">Avbestilling</option>
          <option value="Oppfølging">Oppfølging</option>
          <option value="Akutt">Akutt</option>
        </select>
        <select
          data-testid="call-sentiment-filter"
          value={sentiment}
          onChange={(e) => { setSentiment(e.target.value); setPage(1); }}
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none cursor-pointer"
        >
          <option value="">Alle stemninger</option>
          <option value="Positive">Positiv</option>
          <option value="Neutral">Nøytral</option>
          <option value="Negative">Negativ</option>
        </select>
        <input
          data-testid="call-date-from"
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          title="Fra dato"
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
        />
        <input
          data-testid="call-date-to"
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          title="Til dato"
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
        />
        <button
          data-testid="call-has-notes-toggle"
          onClick={() => { setHasNotes(v => !v); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-xs rounded-lg border transition-colors ${
            hasNotes
              ? 'border-accent-yellow bg-accent-yellow/10 text-accent-yellow'
              : 'border-navy-600 bg-navy-800 text-slate-400 hover:text-white'
          }`}
          title="Vis kun samtaler med notater"
        >
          <StickyNote size={13} />
          Med notater
        </button>
        <button
          data-testid="call-starred-toggle"
          onClick={() => { setShowStarredOnly(v => !v); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-xs rounded-lg border transition-colors ${
            showStarredOnly
              ? 'border-accent-yellow bg-accent-yellow/10 text-accent-yellow'
              : 'border-navy-600 bg-navy-800 text-slate-400 hover:text-white'
          }`}
          title="Vis kun stjernemarkerte samtaler"
        >
          <Star size={13} fill={showStarredOnly ? 'currentColor' : 'none'} />
          Stjernemerket{starredIds.size > 0 ? ` (${starredIds.size})` : ''}
        </button>
        {(dateFrom || dateTo || intent || sentiment || search || hasNotes || showStarredOnly) && (
          <button
            onClick={() => { setSearch(''); setIntent(''); setSentiment(''); setDateFrom(''); setDateTo(''); setHasNotes(false); setShowStarredOnly(false); setPage(1); }}
            className="px-3 py-2.5 text-xs text-slate-400 hover:text-white transition-colors"
            title="Tøm alle filtre"
          >
            Nullstill
          </button>
        )}
        <button
          data-testid="export-csv-button"
          onClick={handleExportCSV}
          disabled={exporting}
          className="flex items-center gap-2 ml-auto px-4 py-2.5 bg-navy-800 border border-navy-600 text-slate-300 text-sm rounded-lg hover:border-accent-blue hover:text-white transition-colors disabled:opacity-50"
        >
          {exporting ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
          {exporting ? t('calls_exporting') : t('calls_export')}
        </button>
        <button
          data-testid="print-button"
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2.5 bg-navy-800 border border-navy-600 text-slate-300 text-sm rounded-lg hover:border-accent-blue hover:text-white transition-colors"
          title="Skriv ut samtaleloggen"
        >
          <Printer size={14} /> Skriv ut
        </button>
      </div>

      {/* Bulk action bar */}
      {bulkSelected.size > 0 && (
        <div data-testid="bulk-action-bar" className="flex items-center justify-between gap-3 mb-4 px-4 py-3 bg-accent-blue/10 border border-accent-blue/30 rounded-lg">
          <div className="flex items-center gap-3 text-sm text-white">
            <span className="font-semibold">{bulkSelected.size}</span> samtaler valgt
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportSelectedCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm rounded-md"
            >
              <Download size={13} /> Eksporter valgte (CSV)
            </button>
            <button
              onClick={() => setBulkSelected(new Set())}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-navy-800 border border-navy-600 text-slate-300 text-sm rounded-md hover:text-white"
            >
              <X size={13} /> Tøm valg
            </button>
          </div>
        </div>
      )}

      {/* Mobile card list (< md screens) */}
      <div className="md:hidden space-y-3" data-testid="calls-mobile-list">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="bg-navy-800 border border-navy-600 rounded-lg p-4 space-y-2">
              <Skeleton height={14} className="w-2/3" />
              <Skeleton height={10} className="w-1/3" />
              <Skeleton height={10} className="w-1/2" />
            </div>
          ))
        ) : (showStarredOnly ? calls.filter(c => starredIds.has(c.call_id)) : calls).length === 0 ? (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-8 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/10 flex items-center justify-center mb-3">
              <Phone size={20} className="text-accent-blue" />
            </div>
            <p className="text-sm font-medium text-white mb-1">{t('calls_no_results')}</p>
            <p className="text-xs text-slate-500">{(search || intent || sentiment || dateFrom || dateTo || hasNotes)
              ? 'Prøv å endre filtrene.'
              : 'Ingen samtaler enda.'}</p>
          </div>
        ) : (showStarredOnly ? calls.filter(c => starredIds.has(c.call_id)) : calls).map((call) => {
          const _ca = call.call_analysis || {}; const a = { ..._ca, ...(_ca.custom_analysis_data || {}), lead_score: call.lead_score ?? _ca.lead_score, urgency_score: call.urgency_score ?? _ca.urgency_score, call_intent: call.call_intent ?? _ca.call_intent, call_summary: call.call_summary ?? _ca.call_summary, sentiment: call.sentiment ?? _ca.sentiment ?? _ca.user_sentiment, caller_name: call.caller_name ?? _ca.caller_name };
          return (
            <div
              key={call._id}
              data-testid={`call-card-${call.call_id}`}
              onClick={() => navigate(`/calls/${call.call_id}`)}
              className="bg-navy-800 border border-navy-600 rounded-lg p-4 active:bg-navy-700 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={(e) => toggleStar(call.call_id, e)}
                    aria-label={starredIds.has(call.call_id) ? 'Fjern stjerne' : 'Stjernemerk'}
                    className={`flex-shrink-0 ${starredIds.has(call.call_id) ? 'text-accent-yellow' : 'text-slate-500'}`}
                  >
                    <Star size={16} fill={starredIds.has(call.call_id) ? 'currentColor' : 'none'} />
                  </button>
                  <span className="text-sm font-medium text-white truncate">{a.caller_name || t('unknown')}</span>
                  {call.notes && <StickyNote size={12} className="text-accent-yellow flex-shrink-0" />}
                </div>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded flex-shrink-0 ${
                  (a.lead_score || 0) >= 80 ? 'bg-accent-green/20 text-accent-green' :
                  (a.lead_score || 0) >= 50 ? 'bg-accent-yellow/20 text-accent-yellow' :
                  'bg-slate-500/20 text-slate-400'
                }`}>{a.lead_score || 0}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400 font-mono mb-1">
                <Phone size={11} /> {call.from_number}
              </div>
              <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                <span className="text-[10px] px-2 py-0.5 rounded bg-navy-700 text-slate-300">{a.call_intent || '—'}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded ${
                  a.sentiment === 'Positive' ? 'bg-accent-green/15 text-accent-green' :
                  a.sentiment === 'Negative' ? 'bg-accent-red/15 text-accent-red' :
                  'bg-slate-500/15 text-slate-400'
                }`}>{a.sentiment || '—'}</span>
                <span className="text-[10px] text-slate-500 ml-auto">{formatDate(call.created_at)}</span>
                <span className="text-[10px] text-slate-400 font-mono">{formatDuration(call.duration_seconds)}</span>
              </div>
            </div>
          );
        })}
        {/* Mobile pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between bg-navy-800 border border-navy-600 rounded-lg px-4 py-3">
            <span className="text-xs text-slate-500">Side {page} av {pages}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-navy-600 text-slate-400 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                className="p-1.5 rounded-lg border border-navy-600 text-slate-400 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Table (md+ screens) */}
      <div className="hidden md:block bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="calls-table">
            <thead>
              <tr className="border-b border-navy-600">
                <th className="text-left px-2 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500 w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleBulkAll}
                    aria-label="Velg alle"
                    data-testid="bulk-select-all"
                    className="w-3.5 h-3.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue"
                  />
                </th>
                <th className="text-left px-2 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500 w-8"></th>
                <SortableTh field="caller_name" sort={sort} order={order} onSort={(f, o) => { setSort(f); setOrder(o); setPage(1); }}>Navn</SortableTh>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Telefon</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Henvendelse</th>
                <SortableTh field="lead_score" sort={sort} order={order} onSort={(f, o) => { setSort(f); setOrder(o); setPage(1); }}>Lead</SortableTh>
                <SortableTh field="urgency_score" sort={sort} order={order} onSort={(f, o) => { setSort(f); setOrder(o); setPage(1); }}>Haster</SortableTh>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Stemning</th>
                <SortableTh field="duration_seconds" sort={sort} order={order} onSort={(f, o) => { setSort(f); setOrder(o); setPage(1); }}>Varighet</SortableTh>
                <SortableTh field="created_at" sort={sort} order={order} onSort={(f, o) => { setSort(f); setOrder(o); setPage(1); }}>Tidspunkt</SortableTh>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-navy-700/30">
                    {[...Array(9)].map((_, j) => (
                      <td key={j} className="px-4 py-4"><Skeleton height={12} className={j === 0 ? 'w-4' : j === 1 ? 'w-32' : 'w-16'} /></td>
                    ))}
                  </tr>
                ))
              ) : visibleCalls.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-16">
                  <div className="flex flex-col items-center justify-center text-center" data-testid="calls-empty-state">
                    <div className="w-14 h-14 rounded-full bg-accent-blue/10 flex items-center justify-center mb-4">
                      <Phone size={22} className="text-accent-blue" />
                    </div>
                    <p className="text-sm font-medium text-white mb-1">{t('calls_no_results')}</p>
                    <p className="text-xs text-slate-500 max-w-sm leading-relaxed">
                      {(search || intent || sentiment || dateFrom || dateTo || hasNotes)
                        ? 'Prøv å endre filtrene eller nullstille søket.'
                        : 'Samtaler dukker opp her så snart AI-resepsjonisten din mottar sin første samtale.'}
                    </p>
                    {(search || intent || sentiment || dateFrom || dateTo || hasNotes || showStarredOnly) && (
                      <button
                        onClick={() => { setSearch(''); setIntent(''); setSentiment(''); setDateFrom(''); setDateTo(''); setHasNotes(false); setShowStarredOnly(false); setPage(1); }}
                        className="mt-4 text-xs px-3 py-1.5 bg-navy-700 hover:bg-navy-600 text-white rounded-md transition-colors"
                      >
                        Nullstill filtre
                      </button>
                    )}
                  </div>
                </td></tr>
              ) : visibleCalls.map((call, idx) => {
                const _ca = call.call_analysis || {}; const a = { ..._ca, ...(_ca.custom_analysis_data || {}), lead_score: call.lead_score ?? _ca.lead_score, urgency_score: call.urgency_score ?? _ca.urgency_score, call_intent: call.call_intent ?? _ca.call_intent, call_summary: call.call_summary ?? _ca.call_summary, buying_signal: call.buying_signal ?? _ca.buying_signal, sentiment: call.sentiment ?? _ca.sentiment ?? _ca.user_sentiment, caller_name: call.caller_name ?? _ca.caller_name, appointment_booked: call.appointment_booked ?? _ca.appointment_booked, callback_requested: call.callback_requested ?? _ca.callback_requested, service_requested: call.service_requested ?? _ca.service_requested, action_items: call.action_items ?? _ca.action_items, is_existing_customer: call.is_existing_customer ?? _ca.is_existing_customer };
                const isSelected = idx === selectedIdx;
                const isChecked = bulkSelected.has(call.call_id);
                return (
                  <tr
                    key={call._id}
                    data-testid={`call-row-${call.call_id}`}
                    onClick={() => navigate(`/calls/${call.call_id}`)}
                    className={`border-b border-navy-600/50 cursor-pointer transition-colors ${isSelected ? 'bg-accent-blue/15 ring-1 ring-accent-blue/50' : isChecked ? 'bg-accent-blue/5' : 'hover:bg-navy-700'}`}
                  >
                    <td className="px-2 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => toggleBulkOne(call.call_id, e)}
                        aria-label="Velg samtale"
                        data-testid={`bulk-select-${call.call_id}`}
                        className="w-3.5 h-3.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue"
                      />
                    </td>
                    <td className="px-2 py-3.5">
                      <button
                        type="button"
                        onClick={(e) => toggleStar(call.call_id, e)}
                        aria-label={starredIds.has(call.call_id) ? 'Fjern stjerne' : 'Stjernemerk'}
                        className={`p-1 rounded hover:bg-navy-600 transition-colors ${starredIds.has(call.call_id) ? 'text-accent-yellow' : 'text-slate-500 hover:text-accent-yellow'}`}
                        data-testid={`star-${call.call_id}`}
                      >
                        <Star size={14} fill={starredIds.has(call.call_id) ? 'currentColor' : 'none'} />
                      </button>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{a.caller_name || t('unknown')}</span>
                        {call.textback_sent && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-400">SMS</span>}
                        {call.notes && (
                          <span title="Har notater" className="text-accent-yellow" data-testid={`note-indicator-${call.call_id}`}>
                            <StickyNote size={12} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-400 font-mono">{call.from_number}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs px-2 py-1 rounded bg-navy-700 text-slate-300">{a.call_intent || '-'}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-sm font-mono font-medium ${
                        (a.lead_score || 0) >= 80 ? 'text-accent-green' :
                        (a.lead_score || 0) >= 50 ? 'text-accent-yellow' : 'text-slate-400'
                      }`}>{a.lead_score || 0}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-sm font-mono ${
                        (a.urgency_score || 0) >= 7 ? 'text-accent-red' :
                        (a.urgency_score || 0) >= 4 ? 'text-accent-yellow' : 'text-slate-400'
                      }`}>{a.urgency_score || 0}/10</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs px-2 py-1 rounded ${
                        a.sentiment === 'Positive' ? 'bg-accent-green/15 text-accent-green' :
                        a.sentiment === 'Negative' ? 'bg-accent-red/15 text-accent-red' :
                        'bg-slate-500/15 text-slate-400'
                      }`}>{a.sentiment || '-'}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-400 font-mono">{formatDuration(call.duration_seconds)}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-400">{formatDate(call.created_at)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-navy-600">
            <span className="text-xs text-slate-500">Side {page} av {pages}</span>
            <div className="flex items-center gap-2">
              <button
                data-testid="pagination-prev"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-navy-600 text-slate-400 hover:text-white hover:border-accent-blue disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                data-testid="pagination-next"
                onClick={() => setPage(p => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="p-1.5 rounded-lg border border-navy-600 text-slate-400 hover:text-white hover:border-accent-blue disabled:opacity-30 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
